import { useDataset } from "@/hooks/map/useDataset";
import { useAnalysisLegendConfig } from "@/hooks/spatialAnalysis/useAnalysisLegendConfig";
import { Dataset } from "@/types/dataset";

import SliderInput from "./SliderInput";

type OpacitySliderProps = {
  data: Dataset;
  isSpatialAnalysis?: boolean;
  type?: "fill" | "label" | "stroke";
  disabled?: boolean;
};

export default function OpacitySlider(props: Readonly<OpacitySliderProps>) {
  const { data, type, disabled } = props;
  const {
    handleChangeOpacityDataset,
    handleChangeLabelOpacity,
    handleChangeStrokeOpacityDataset
  } = useDataset();
  const { persistAnalysisLegendConfigDebounced } = useAnalysisLegendConfig();

  const applyOpacity = (opacity: number) => {
    if (type === "label") {
      // Label opacity is not part of the saved legend, so it is not persisted.
      handleChangeLabelOpacity(data, opacity);
      return;
    }
    if (type === "stroke") {
      handleChangeStrokeOpacityDataset(data.data.id, opacity);
    } else {
      handleChangeOpacityDataset(data.data.id, opacity);
    }
    // The layer opacity is part of the saved legend after an analysis; the save
    // waits for the slider to settle, and is a no-op everywhere else.
    persistAnalysisLegendConfigDebounced();
  };

  const handleChangeOpacity = (value: number) => {
    if (value > 100) value = 100;
    if (value < 0) value = 0;
    applyOpacity(value / 100);
  };

  const handleChangeOpacitySlider = (value: number[]) =>
    applyOpacity(value[0] / 100);

  const getOpacity = () => {
    if (type === "label") return data?.style?.labelConfig?.opacity || 0;
    if (type === "stroke") return data?.style?.stroke?.opacity || 0;
    return data.style.fillOpacity;
  };

  const opacity = getOpacity();
  const opacityValue = [opacity * 100];
  const opacityInputValue = Math.round(opacity * 100);

  return (
    <div className="flex flex-col gap-2 w-full">
      <p className="text-xs text-txtPlaceholder">Opacity</p>
      <SliderInput
        valueSlider={opacityValue}
        onChangeSlider={handleChangeOpacitySlider}
        valueInput={opacityInputValue}
        onChangeInput={handleChangeOpacity}
        disabled={disabled}
      />
    </div>
  );
}
